import React from "react";
import {
  Stack,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Fade,
  Chip,
} from "@mui/material";
import { PlanOption } from "../PlanReviewPage";
import { toLocaleDateTimeString } from "@utils";
import { getCompletedPlanTimestamps } from "@volatile/pages/OrderTracker/components";
import {
  PlanSelector,
  Scorecard,
  getComparisonScorecardData,
  DVHCard,
  VideoWalkthrough,
} from "@volatile/components";

interface PlanSelectionHook {
  selectedPlanOption: string;
  setSelectedPlanOption: (value: string) => void;
  selectedPlan: string;
  setSelectedPlan: (value: string) => void;
  selectedPlanAOption: string;
  setSelectedPlanAOption: (value: string) => void;
  selectedPlanBOption: string;
  setSelectedPlanBOption: (value: string) => void;
  planOptions: PlanOption[];
  getPlanDisplayName: (plan: string) => string;
  getAvailablePlanAOptions: () => PlanOption[];
  getAvailablePlanBOptions: () => PlanOption[];
}

interface ComparisonViewProps {
  orderId: number;
  scorecardAttachmentsLoading: boolean;
  stickyTopOffset?: number;
  dateLabel?: string;
  planSelection: PlanSelectionHook;
  planGenerationData?: unknown[];
  isPhysicianReview?: boolean;
}

const ComparisonView: React.FC<ComparisonViewProps> = ({
  orderId,
  scorecardAttachmentsLoading,
  stickyTopOffset = 0,
  dateLabel,
  planSelection,
  planGenerationData: _planGenerationData,
  isPhysicianReview = false,
}) => {
  // Function to get timestamps for Plan A and Plan B
  const getPlanTimestamp = (planKey: string) => {
    // Always use localStorage timestamps to match CompletedPlans table (ignore planGenerationData)
    const timestamps = getCompletedPlanTimestamps();

    // Explicit mapping to ensure consistency with CompletedPlans table
    // planA → "Prostate Nodes R1" → plan1FinishedAt
    // planB → "Prostate Nodes R2" → plan2FinishedAt
    if (planKey === "planA") {
      return toLocaleDateTimeString(timestamps.plan1FinishedAt);
    } else if (planKey === "planB") {
      return toLocaleDateTimeString(timestamps.plan2FinishedAt);
    }

    // Fallback by plan display name
    const planDisplayName = planSelection.getPlanDisplayName(planKey);
    if (planDisplayName === "Prostate Nodes R1") {
      return toLocaleDateTimeString(timestamps.plan1FinishedAt);
    } else if (planDisplayName === "Prostate Nodes R2") {
      return toLocaleDateTimeString(timestamps.plan2FinishedAt);
    }

    // Return different fallbacks for Plan A vs Plan B
    return planKey === "planA"
      ? "Mar-16-2024, 1:30 PM"
      : "Mar-17-2024, 3:44 PM";
  };

  return (
    <Fade in timeout={500}>
      <Stack>
        {/* Plan Selection Section - Sticky */}
        <Stack
          gap={2}
          marginBottom={1}
          sx={{
            position: "sticky",
            top: stickyTopOffset,
            backgroundColor: "background.default",
            zIndex: 100,
            paddingTop: 2,
            paddingBottom: 2,
            marginTop: -2,
            marginLeft: -8,
            marginRight: -8,
            paddingLeft: 8,
            paddingRight: 8,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          {/* Plan Selector */}
          <PlanSelector
            mode="comparison"
            selectedPlan={planSelection.selectedPlan}
            selectedPlanAOption={planSelection.selectedPlanAOption}
            selectedPlanBOption={planSelection.selectedPlanBOption}
            onPlanAOptionChange={planSelection.setSelectedPlanAOption}
            onPlanBOptionChange={planSelection.setSelectedPlanBOption}
            availablePlanAOptions={planSelection.getAvailablePlanAOptions()}
            availablePlanBOptions={planSelection.getAvailablePlanBOptions()}
            planOptions={planSelection.planOptions}
            dateLabel={dateLabel}
            planATimestamp={getPlanTimestamp(planSelection.selectedPlanAOption)}
            planBTimestamp={getPlanTimestamp(planSelection.selectedPlanBOption)}
          />
        </Stack>

        {/* Title Section */}
        <Stack marginBottom={3}>
          <Typography variant="h5">Select Plan</Typography>
          <Typography variant="body2" color="text.secondary">
            Select the most suitable plan for plan review.
          </Typography>
        </Stack>

        {/* Plan Descriptions */}
        <RadioGroup
          value={planSelection.selectedPlan}
          onChange={(e) => planSelection.setSelectedPlan(e.target.value)}
        >
          <Stack direction="row" gap={3} marginBottom={3}>
            <Stack
              flex={1}
              padding={2}
              border="2px solid"
              borderColor={
                planSelection.selectedPlan === "planA"
                  ? "primary.main"
                  : "#e0e0e0"
              }
              borderRadius={1}
              sx={{
                cursor: "pointer",
                position: "relative",
                backgroundColor:
                  planSelection.selectedPlan === "planA"
                    ? "rgba(116, 93, 229, 0.12)"
                    : "transparent",
              }}
              onClick={() => planSelection.setSelectedPlan("planA")}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                marginBottom={2}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography variant="h6">Plan A</Typography>
                  {isPhysicianReview && (
                    <Chip
                      label="Recommended by Dosimetrist"
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 20,
                        fontSize: "0.75rem",
                        fontWeight: 400,
                        borderColor: "#374151 !important",
                        color: "#374151 !important",
                        "& .MuiChip-label": {
                          color: "#374151 !important",
                        },
                        "&.MuiChip-outlined": {
                          borderColor: "#374151 !important",
                        },
                      }}
                    />
                  )}
                </Stack>
                <FormControlLabel
                  value="planA"
                  control={<Radio />}
                  label=""
                  sx={{ margin: 0 }}
                />
              </Stack>
              <Typography variant="body2">
                This plan achieves the required target coverage across CTV’s and
                PTV’s while achieving all Rectum and Bowel constraints. There
                are minor deviations on the Bladder which is expected given the
                overlap with the Targets. All other organs at risk are within
                the specified tolerance
              </Typography>
            </Stack>
            <Stack
              flex={1}
              padding={2}
              border="2px solid"
              borderColor={
                planSelection.selectedPlan === "planB"
                  ? "primary.main"
                  : "#e0e0e0"
              }
              borderRadius={1}
              sx={{
                cursor: "pointer",
                position: "relative",
                backgroundColor:
                  planSelection.selectedPlan === "planB"
                    ? "rgba(116, 93, 229, 0.12)"
                    : "transparent",
              }}
              onClick={() => planSelection.setSelectedPlan("planB")}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                marginBottom={2}
              >
                <Typography variant="h6">Plan B</Typography>
                <FormControlLabel
                  value="planB"
                  control={<Radio />}
                  label=""
                  sx={{ margin: 0 }}
                />
              </Stack>
              <Typography variant="body2">
                This plan achieves the required target coverage across CTV’s and
                PTV’s, however the rectum dose is higher than Plan A. The volume
                of bowel receiving 45Gy is higher and Bladder doses are in the
                minor deviation region. All other organs at risk are within the
                specified tolerance.
              </Typography>
            </Stack>
          </Stack>
        </RadioGroup>

        {/* Cards with spacing */}
        <Stack gap={3} sx={{ marginBottom: 4 }}>
          {/* Scorecard */}
          <Scorecard
            mode="comparison"
            data={getComparisonScorecardData()}
            isLoading={scorecardAttachmentsLoading}
          />

          {/* Dose Volume Histogram */}
          <DVHCard isComparisonMode={true} />

          {/* Dose Distribution Walkthrough */}
          <VideoWalkthrough orderId={orderId} isComparisonMode={true} />
        </Stack>
      </Stack>
    </Fade>
  );
};

export default ComparisonView;
